// Oct 29 2026 · Karine — stationary photo "menu boxes" replacing the rolling
// Trending-Now pills. One tap = that treatment's page, nothing else. Same
// footprint as the old strip on desktop; on phones a compact 4-across grid.
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Tag } from "lucide-react";
import { TREATMENT_CATEGORIES } from "@/data/treatmentTaxonomy";
import { useLanguage } from "@/context/LanguageContext";

const SHORT = {
  prevention: { en: "Prevention", fr: "Prévention" },
  botox: { en: "Botox", fr: "Botox" },
  fillers: { en: "Dermal Fillers", fr: "Fillers" },
  sculptra: { en: "Sculptra", fr: "Sculptra" },
  radiesse: { en: "Radiesse", fr: "Radiesse" },
  "skin-boosters": { en: "Skin Boosters", fr: "Boosters" },
  "womens-health": { en: "Women's & Men's Health", fr: "Santé Femmes & Hommes" },
  "laser-skin": { en: "Laser & Skin", fr: "Laser & Peau" },
  "laser-hair": { en: "Laser Hair Removal", fr: "Épilation Laser" },
  "facials-body": { en: "Facials & Body", fr: "Soins & Corps" },
  vip: { en: "VIP & Memberships", fr: "VIP & Adhésions" },
};

const ORDER = ["promotions", "botox", "fillers", "sculptra", "radiesse", "skin-boosters", "laser-skin", "laser-hair", "facials-body", "womens-health", "vip", "prevention"];
// "Book now" chip target per box — a filtered treatment picker (no auto-pick)
const BOOK = {
  promotions: "/book?filter=Promo",
  botox: "/book?filter=Botox",
  fillers: "/book?filter=Filler",
  sculptra: "/book?filter=Sculptra",
  radiesse: "/book?filter=Radiesse",
  "skin-boosters": "/book?filter=Redensity",
  "laser-skin": "/book?filter=Laser",
  "laser-hair": "/book?filter=Laser%20Hair",
  "facials-body": "/book?filter=Facial",
  "womens-health": "/book?filter=Consult",
  vip: "/crown-reserve-plan",
  prevention: "/book?filter=Consult",
};

const BOXES = [
  ...TREATMENT_CATEGORIES.map((c) => ({ id: c.id, href: c.landingHref, label: SHORT[c.id] || c.label })),
  { id: "promotions", href: "/promotions", label: { en: "Promotions", fr: "Promotions" }, promo: true },
].sort((a, b) => ORDER.indexOf(a.id) - ORDER.indexOf(b.id));

export default function TreatmentBoxMenu({ className = "" }) {
  const { language } = useLanguage();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const fr = language === "fr";
  return (
    <nav aria-label={fr ? "Menu des traitements" : "Treatment menu"} className={`w-full px-2 sm:px-4 -mt-2.5 ${className}`} data-testid="treatment-box-menu">
      <ul className="grid grid-cols-4 sm:grid-cols-6 xl:grid-cols-12 gap-1.5 sm:gap-2">
        {BOXES.map((b) => {
          const active = pathname === b.href || (b.href !== "/" && pathname.startsWith(b.href + "/"));
          return (
            <li key={b.id}>
              <Link
                to={b.href}
                onClick={() => window.scrollTo(0, 0)}
                aria-current={active ? "page" : undefined}
                className={`group relative block h-14 sm:h-16 xl:h-[72px] rounded-xl overflow-hidden ring-1 transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${
                  active ? "ring-2 ring-amber-300 shadow-[0_0_0_3px_rgba(252,211,77,0.25)]" : b.promo ? "ring-amber-400/70" : "ring-white/15"
                }`}
                data-testid={`menu-box-${b.id}`}
              >
                <img src={`/menu-boxes/${b.id}.jpg`} alt={`${b.id.replace(/-/g, " ")} — Karine Lauzon RN Clinics`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <span className={`absolute inset-0 ${b.promo ? "bg-gradient-to-t from-amber-950/95 via-amber-900/55 to-amber-900/20" : "bg-gradient-to-t from-zinc-950/95 via-zinc-900/55 to-zinc-900/15"}`} aria-hidden="true" />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.scrollTo(0, 0); navigate(BOOK[b.id] || "/book"); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); navigate(BOOK[b.id] || "/book"); } }}
                  className="absolute top-1 right-1 sm:top-1.5 sm:right-1.5 rounded-full bg-[#F3EADA] hover:bg-white text-[#3A2A14] text-[8px] sm:text-[9px] font-black uppercase tracking-wider px-1.5 sm:px-2 py-0.5 leading-none shadow ring-1 ring-black/10"
                  data-testid={`menu-box-book-${b.id}`}
                >
                  {fr ? "Réserver" : "Book now"}
                </span>
                <span className="absolute inset-x-1 bottom-1 sm:bottom-1.5 flex items-center justify-center gap-1 text-center">
                  {b.promo && <Tag size={11} className="text-amber-300 shrink-0" aria-hidden="true" />}
                  <span className={`text-[10px] sm:text-[11px] xl:text-xs font-bold leading-tight tracking-wide drop-shadow ${b.promo ? "text-amber-200" : "text-white"}`}>
                    {fr ? b.label.fr : b.label.en}
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
